import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import {
  TrophyIcon,
  CalendarDaysIcon,
  CheckCircleIcon,
  ClockIcon,
  DocumentTextIcon,
  ArrowsRightLeftIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import subscriptionAPI from '../../services/subscriptionAPI';
import { subscriptionAPI as newSubscriptionAPI } from '../../config/api';

const SubscriptionManagement = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, lab } = useSelector((state) => state.auth);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [exchangeCredits, setExchangeCredits] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Redirect if not lab user
    if (!user || user.role !== 'lab') {
      navigate('/');
      return;
    }

    // Redirect non-premium labs to subscription plans
    const isLabPremium = lab?.isPremium && lab?.premiumUntil && new Date(lab.premiumUntil) > new Date();
    if (!isLabPremium) {
      navigate('/subscription-plans');
      return;
    }

    fetchSubscriptionStatus();
  }, [user, lab, navigate]);

  const fetchSubscriptionStatus = async () => {
    try {
      const [subscriptionResponse, creditsResponse] = await Promise.all([
        subscriptionAPI.getSubscriptionStatus(),
        newSubscriptionAPI.getExchangeCredits()
      ]);
      
      setSubscriptionData(subscriptionResponse);
      setExchangeCredits(creditsResponse.data);
    } catch (error) {
      console.error('Failed to fetch subscription status:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const locale = t('common.locale') === 'ar' ? 'ar-SA' : 'en-US';
    return date.toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-blue"></div>
      </div>
    );
  }

  const subscription = subscriptionData?.subscription;

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-brand-blue to-brand-green rounded-3xl p-8 text-white"
      >
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <TrophyIcon className="h-8 w-8" />
              <h1 className="text-3xl font-bold">{t('lab.subscriptionManagement.title')}</h1>
            </div>
            <p className="text-lg opacity-90">
              {t('lab.subscriptionManagement.subtitle')}
            </p>
          </div>
          <div className="text-right">
            <div className="bg-white/20 rounded-2xl px-6 py-4">
              <p className="text-sm opacity-75">{t('lab.subscriptionManagement.subscriptionStatus')}</p>
              <div className="flex items-center gap-2 mt-1">
                <CheckCircleIcon className="h-5 w-5 text-green-300" />
                <span className="text-lg font-bold">{t('lab.subscriptionManagement.active')}</span>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Subscription Details */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8"
      >
        <div className="flex items-center gap-3 mb-6">
          <CalendarDaysIcon className="h-6 w-6 text-brand-blue" />
          <h2 className="text-2xl font-bold text-gray-900">{t('lab.subscriptionManagement.subscriptionDetails')}</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          {/* Start Date */}
          <div className="bg-gray-50 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-brand-blue/10 rounded-full flex items-center justify-center">
                <CalendarDaysIcon className="h-5 w-5 text-brand-blue" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">{t('lab.subscriptionManagement.startDate')}</h3>
                <p className="text-sm text-gray-500">{t('lab.subscriptionManagement.startDateDesc')}</p>
              </div>
            </div>
            <p className="text-lg font-bold text-gray-900">
              {formatDate(subscription?.startDate)}
            </p>
          </div>

          {/* End Date */}
          <div className="bg-gray-50 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-brand-green/10 rounded-full flex items-center justify-center">
                <ClockIcon className="h-5 w-5 text-brand-green" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">{t('lab.subscriptionManagement.endDate')}</h3>
                <p className="text-sm text-gray-500">{t('lab.subscriptionManagement.endDateDesc')}</p>
              </div>
            </div>
            <p className="text-lg font-bold text-gray-900">
              {formatDate(subscription?.endDate)}
            </p>
          </div>

          {/* Days Remaining */}
          <div className="bg-gradient-to-r from-brand-blue/5 to-brand-green/5 rounded-2xl p-6 border border-brand-blue/20">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-brand-blue rounded-full flex items-center justify-center">
                <ClockIcon className="h-5 w-5 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-brand-blue">{t('lab.subscriptionManagement.daysRemaining')}</h3>
                <p className="text-sm text-gray-500">{t('lab.subscriptionManagement.daysRemainingDesc')}</p>
              </div>
            </div>
            <p className="text-2xl font-bold text-brand-blue">
              {subscription?.daysRemaining || 0} {t('lab.subscriptionManagement.daysUnit')}
            </p>
          </div>

          {/* Exchange Credits */}
          <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-2xl p-6 border-2 border-green-200">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-blue-500 rounded-full flex items-center justify-center">
                <ArrowsRightLeftIcon className="h-5 w-5 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-green-700">رصيد التبادلات المجانية</h3>
                <p className="text-sm text-gray-500">التبادلات المتبقية هذا العام</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <p className="text-2xl font-bold text-green-700">
                {exchangeCredits?.exchangeCredits?.available || 0}
              </p>
              <div className="flex items-center gap-1">
                <SparklesIcon className="h-4 w-4 text-yellow-500" />
                <span className="text-sm text-gray-600">من أصل 10</span>
              </div>
            </div>
            {exchangeCredits?.exchangeCredits?.resetDate && (
              <p className="text-xs text-gray-500 mt-2">
                يتم التجديد في: {formatDate(exchangeCredits.exchangeCredits.resetDate)}
              </p>
            )}
          </div>

          {/* Invoice Number */}
          <div className="bg-gray-50 rounded-2xl p-6">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-gray-400/10 rounded-full flex items-center justify-center">
                <DocumentTextIcon className="h-5 w-5 text-gray-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">{t('lab.subscriptionManagement.invoiceNumber')}</h3>
                <p className="text-sm text-gray-500">{t('lab.subscriptionManagement.invoiceDesc')}</p>
              </div>
            </div>
            <p className="text-lg font-bold text-gray-900">
              {subscription?.invoiceNumber || t('lab.subscriptionManagement.notAvailable')}
            </p>
          </div>
        </div>
      </motion.div>

      {/* Benefits */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8"
      >
        <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('lab.subscriptionManagement.activeBenefits')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center p-6 bg-green-50 rounded-2xl">
            <CheckCircleIcon className="h-12 w-12 text-green-600 mx-auto mb-4" />
            <h3 className="font-bold text-gray-900 mb-2">{t('lab.subscriptionManagement.commissionExemption')}</h3>
            <p className="text-gray-600">{t('lab.subscriptionManagement.commissionExemptionDesc')}</p>
          </div>
          
          <div className="text-center p-6 bg-blue-50 rounded-2xl">
            <div className="relative">
              <ArrowsRightLeftIcon className="h-12 w-12 text-blue-600 mx-auto mb-4" />
              {exchangeCredits?.exchangeCredits?.available > 0 && (
                <div className="absolute -top-1 -right-1 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-xs font-bold">{exchangeCredits.exchangeCredits.available}</span>
                </div>
              )}
            </div>
            <h3 className="font-bold text-gray-900 mb-2">تبادلات مجانية</h3>
            <p className="text-gray-600">
              {exchangeCredits?.exchangeCredits?.available > 0 
                ? `لديك ${exchangeCredits.exchangeCredits.available} تبادلات مجانية متبقية`
                : "تم استنفاد التبادلات المجانية لهذا العام"
              }
            </p>
          </div>
          
          <div className="text-center p-6 bg-purple-50 rounded-2xl">
            <CheckCircleIcon className="h-12 w-12 text-purple-600 mx-auto mb-4" />
            <h3 className="font-bold text-gray-900 mb-2">{t('lab.subscriptionManagement.premiumSupport')}</h3>
            <p className="text-gray-600">{t('lab.subscriptionManagement.premiumSupportDesc')}</p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default SubscriptionManagement;
